/**
 * Rack Configuration Types (Frontend)
 * TypeScript interfaces and Zod schemas for multi-rack feature (#995)
 *
 * IMPORTANT: Zod schemas must be wrapped in useMemo() for iOS to avoid crashes
 * @see GitLab #995 — Multiple Rack Instances
 */

import { z } from 'zod';

export type RackStatus = 'existing' | 'new';

/**
 * Rack Configuration interface (camelCase — matches API response)
 */
export interface RackConfiguration {
  id?: number;
  requestId: number;

  // AC4: Existing vs New status
  rackStatus: RackStatus;

  // User-defined rack name (#995 Rack Naming)
  rackName?: string;

  // Rack Core Fields (7 fields)
  rackPlatform?: string;
  rackQuantity?: number;
  rackDimensionsLength?: number;
  rackDimensionsHeight?: number;
  rackDimensionsWidth?: number;
  rackAdditionalRackInformation?: string;

  // Rack Info Fields - #957 Story 4.1 (7 fields)
  rackDescription?: string;
  rackUse?: string;
  rackSeismicRated?: boolean;
  rackMaxLoadRating?: string;
  rackColor?: string;
  rackRequiresLiquidToChip?: boolean;
  rackDescribeElevations?: string;

  // PDU Fields - Original (7 fields)
  rackPduMountingType?: string;
  rackPduCountPerRack?: string;
  rackPduOutletSwitchingMonitored?: string;
  rackPduOutletTypesQuantity?: string;
  rackPduPowerType?: string;
  rackPduMaxKwPerRack?: string;
  rackPduAdditionalInfo?: string;

  // PDU Fields - #957 Story 4.1 (9 fields)
  rackPduManufacturer?: string;
  rackPduModel?: string;
  rackPduInputPlugType?: string;
  rackPduDcPowerShelves?: number;
  rackPduCapacityAmps?: number;
  rackPduCapacityVolts?: number;
  rackPduCapacityKw?: number;
  rackPduLoadReadingsKw?: string;
  rackPduLocation?: string;

  /** Facility location tag assignment (#999) */
  locationTagId?: number | null;

  // Metadata
  displayOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Input for creating a new rack configuration
 */
export type CreateRackConfigurationInput = Omit<
  RackConfiguration,
  'id' | 'createdAt' | 'updatedAt'
>;

/**
 * Input for updating a rack configuration
 */
export type UpdateRackConfigurationInput = Partial<
  Omit<RackConfiguration, 'id' | 'requestId' | 'createdAt' | 'updatedAt'>
>;

/**
 * Grouped rack configurations for AC5 segmented view
 */
export interface GroupedRackConfigurations {
  existing: RackConfiguration[];
  new: RackConfiguration[];
}

/**
 * API response for rack configurations list
 */
export interface RackConfigurationsResponse {
  racks: RackConfiguration[];
  total: number;
}

/**
 * Zod schema factory for rack configuration validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 *
 * @example
 * const schema = useMemo(() => createRackConfigurationSchema(), []);
 */
export const createRackConfigurationSchema = () =>
  z.object({
    id: z.number().optional(),
    requestId: z.number(),
    rackStatus: z.enum(['existing', 'new']),

    // User-defined rack name
    rackName: z.string().optional(),

    // Rack Core Fields
    rackPlatform: z.string().optional(),
    rackQuantity: z.number().min(0).optional(),
    rackDimensionsLength: z.number().min(0).optional(),
    rackDimensionsHeight: z.number().min(0).optional(),
    rackDimensionsWidth: z.number().min(0).optional(),
    rackAdditionalRackInformation: z.string().optional(),

    // Rack Info Fields
    rackDescription: z.string().optional(),
    rackUse: z.string().optional(),
    rackSeismicRated: z.boolean().optional(),
    rackMaxLoadRating: z.string().optional(),
    rackColor: z.string().optional(),
    rackRequiresLiquidToChip: z.boolean().optional(),
    rackDescribeElevations: z.string().optional(),

    // PDU Fields - Original
    rackPduMountingType: z.string().optional(),
    rackPduCountPerRack: z.string().optional(),
    rackPduOutletSwitchingMonitored: z.string().optional(),
    rackPduOutletTypesQuantity: z.string().optional(),
    rackPduPowerType: z.string().optional(),
    rackPduMaxKwPerRack: z.string().optional(),
    rackPduAdditionalInfo: z.string().optional(),

    // PDU Fields - Extended
    rackPduManufacturer: z.string().optional(),
    rackPduModel: z.string().optional(),
    rackPduInputPlugType: z.string().optional(),
    rackPduDcPowerShelves: z.number().min(0).optional(),
    rackPduCapacityAmps: z.number().min(0).optional(),
    rackPduCapacityVolts: z.number().min(0).optional(),
    rackPduCapacityKw: z.number().min(0).optional(),
    rackPduLoadReadingsKw: z.string().optional(),
    rackPduLocation: z.string().optional(),
    locationTagId: z.number().int().nullable().optional(),

    // Metadata
    displayOrder: z.number().min(0),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  });

/**
 * Zod schema factory for create rack input validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 */
export const createRackInputSchema = () =>
  createRackConfigurationSchema().omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  });

/**
 * Zod schema factory for update rack input validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 */
export const updateRackInputSchema = () =>
  createRackConfigurationSchema()
    .omit({
      id: true,
      requestId: true,
      createdAt: true,
      updatedAt: true,
    })
    .partial();

/**
 * Type inference from Zod schemas
 */
export type RackConfigurationSchema = z.infer<
  ReturnType<typeof createRackConfigurationSchema>
>;
export type CreateRackInputSchema = z.infer<
  ReturnType<typeof createRackInputSchema>
>;
export type UpdateRackInputSchema = z.infer<
  ReturnType<typeof updateRackInputSchema>
>;

/**
 * Dropdown options for rack fields (kept in UI per architecture decision)
 */
export const RACK_PLATFORM_OPTIONS = ['Standard', 'OCP/ORV3', 'MGX'] as const;
export const RACK_MAX_LOAD_RATING_OPTIONS = [
  '500 kg',
  '1000 kg',
  '1500 kg',
  '2000 kg',
  '3000 kg',
  '4000 kg',
  'Other',
] as const;
export const RACK_COLOR_OPTIONS = ['Black', 'White', 'Custom'] as const;
export const RACK_PDU_MOUNTING_TYPE_OPTIONS = [
  'Horizontal',
  'Vertical',
] as const;
export const RACK_PDU_OUTLET_TYPE_OPTIONS = [
  'Unit Monitored',
  'Outlet Monitored',
  'Switching',
] as const;
export const RACK_STATUS_OPTIONS: RackStatus[] = ['existing', 'new'];

/**
 * Default values for a new rack configuration
 */
export const DEFAULT_RACK_CONFIGURATION: Partial<RackConfiguration> = {
  rackStatus: 'new',
  rackQuantity: 1,
  displayOrder: 0,
};
